// ---------------------------------------------------------------------------
// Theme Slice — project application design tokens and design-system axes
//
// Application design commands mutate currentProject.tokens only. They must not
// write Studio shell/root tokens or change the Studio design-system axes. The
// legacy setTheme*/updateTheme* aliases below delegate to these app-scoped
// commands for backward compatibility with older tests and chat actions.
// ---------------------------------------------------------------------------

import {
  DEFAULT_DESIGN_SYSTEM,
  type DesignSystemState,
  STYLE_OPTIONS,
  BASE_COLOR_OPTIONS,
  THEME_OPTIONS,
  CHART_OPTIONS,
  RADIUS_OPTIONS,
  FONT_OPTIONS,
} from '@/features/design-system/lib/design-system-options'
import { markProjectContentDirty } from './project-content-dirty'

export interface ThemePreset {
  id: string
  name: string
  colors: Record<string, string>
  fontFamily: string
  radius: 'none' | 'sm' | 'md' | 'lg'
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'modern',
    name: 'Modern',
    colors: {
      background: '#ffffff', foreground: '#0a0a0a',
      primary: '#4F46E5', primaryForeground: '#ffffff',
      secondary: '#f5f5f5', secondaryForeground: '#0a0a0a',
      accent: '#f5f5f5', accentForeground: '#0a0a0a',
      muted: '#f5f5f5', mutedForeground: '#737373',
      destructive: '#ef4444', destructiveForeground: '#ffffff',
      border: '#e5e5e5', input: '#e5e5e5', ring: '#4F46E5',
      card: '#ffffff', cardForeground: '#0a0a0a',
      popover: '#ffffff', popoverForeground: '#0a0a0a',
    },
    fontFamily: 'Inter',
    radius: 'md',
  },
  {
    id: 'warm',
    name: 'Warm',
    colors: {
      background: '#FFFBEB', foreground: '#1C1917',
      primary: '#D97706', primaryForeground: '#ffffff',
      secondary: '#FEF3C7', secondaryForeground: '#1C1917',
      accent: '#FDE68A', accentForeground: '#1C1917',
      muted: '#F5F5F4', mutedForeground: '#78716C',
      destructive: '#DC2626', destructiveForeground: '#ffffff',
      border: '#E7E5E4', input: '#E7E5E4', ring: '#D97706',
      card: '#ffffff', cardForeground: '#1C1917',
      popover: '#ffffff', popoverForeground: '#1C1917',
    },
    fontFamily: 'DM Sans',
    radius: 'lg',
  },
  {
    id: 'professional',
    name: 'Professional',
    colors: {
      background: '#ffffff', foreground: '#0f172a',
      primary: '#475569', primaryForeground: '#ffffff',
      secondary: '#f1f5f9', secondaryForeground: '#0f172a',
      accent: '#e2e8f0', accentForeground: '#0f172a',
      muted: '#f8fafc', mutedForeground: '#64748b',
      destructive: '#ef4444', destructiveForeground: '#ffffff',
      border: '#e2e8f0', input: '#e2e8f0', ring: '#475569',
      card: '#ffffff', cardForeground: '#0f172a',
      popover: '#ffffff', popoverForeground: '#0f172a',
    },
    fontFamily: 'IBM Plex Sans',
    radius: 'sm',
  },
  {
    id: 'vibrant',
    name: 'Sky',
    colors: {
      background: '#f8fafc', foreground: '#0f172a',
      primary: '#0EA5E9', primaryForeground: '#ffffff',
      secondary: '#e0f2fe', secondaryForeground: '#0369a1',
      accent: '#bae6fd', accentForeground: '#0369a1',
      muted: '#f1f5f9', mutedForeground: '#64748b',
      destructive: '#e11d48', destructiveForeground: '#ffffff',
      border: '#bae6fd', input: '#bae6fd', ring: '#0EA5E9',
      card: '#ffffff', cardForeground: '#0f172a',
      popover: '#ffffff', popoverForeground: '#0f172a',
    },
    fontFamily: 'Pretendard',
    radius: 'lg',
  },
  {
    id: 'minimal',
    name: 'Minimal',
    colors: {
      background: '#ffffff', foreground: '#18181b',
      primary: '#18181b', primaryForeground: '#fafafa',
      secondary: '#f4f4f5', secondaryForeground: '#18181b',
      accent: '#f4f4f5', accentForeground: '#18181b',
      muted: '#f4f4f5', mutedForeground: '#71717a',
      destructive: '#ef4444', destructiveForeground: '#ffffff',
      border: '#e4e4e7', input: '#e4e4e7', ring: '#18181b',
      card: '#ffffff', cardForeground: '#18181b',
      popover: '#ffffff', popoverForeground: '#18181b',
    },
    fontFamily: 'Geist',
    radius: 'sm',
  },
]

/**
 * Radius token scales for the GENERATED APPLICATION — keyed by the preset's
 * own `radius` vocabulary (`none|sm|md|lg`), NOT by the Studio design-system
 * radius axis (`default|none|small|medium|large`, see `RADII_LIST`). The two
 * vocabularies overlap on exactly one name (`none`), which is how a table
 * written against this one can be indexed by the other and look plausible.
 *
 * The annotation is `Record<ThemePreset['radius'], …>` rather than
 * `Record<string, …>` ON PURPOSE. An over-wide `Record<string, …>` accepts any
 * key set at all, so a scale added to `ThemePreset['radius']` and forgotten
 * here would compile clean and be swallowed by the `?? RADIUS_MAP.md` at every
 * read site. Keyed by the union, that omission is a compile error instead.
 * (This is the defect that killed `customizer/radius-selector.tsx`: a
 * `Record<string, string>` holding THIS vocabulary, indexed by the AXIS one,
 * so four of five previews silently fell through to the fallback.)
 */
export const RADIUS_MAP: Record<ThemePreset['radius'], Record<string, string>> = {
  none: { none: '0px', sm: '0px', md: '0px', lg: '0px', xl: '0px', full: '0px' },
  sm:   { none: '0px', sm: '2px', md: '4px', lg: '6px', xl: '8px', full: '9999px' },
  md:   { none: '0px', sm: '4px', md: '8px', lg: '12px', xl: '16px', full: '9999px' },
  lg:   { none: '0px', sm: '6px', md: '12px', lg: '16px', xl: '24px', full: '9999px' },
}

/**
 * Narrow a free-form scale name onto `RADIUS_MAP`'s vocabulary.
 *
 * The MCP `setRadius` tool and the `theme.radius.set` capability both take an
 * unconstrained `string`, so callers CAN hand us a name from another radius
 * vocabulary — `medium` from the design-system axis is the obvious one. That
 * has to fall back, but the fallback belongs at one named site that says so,
 * not spread across `RADIUS_MAP[scale] ?? RADIUS_MAP.md` at each read, where
 * it is indistinguishable from a table that simply drifted.
 *
 * Membership is tested against `RADIUS_MAP`'s OWN keys, so this cannot drift
 * away from the table it guards.
 */
export function resolveRadiusScale(scale: string): Record<string, string> {
  return Object.prototype.hasOwnProperty.call(RADIUS_MAP, scale)
    ? RADIUS_MAP[scale as ThemePreset['radius']]
    : RADIUS_MAP.md
}

// NOTE: FONT_OPTIONS is imported from `@/features/design-system/lib/design-system-options`
// (LabeledOption[] with `{value, label}` shape). A previous stray `as const` string tuple
// declaration here shadowed the import and broke shuffleDesignSystem's pick() generic
// (Rule 1 bug — TS2345 fix in Plan 002-03).

export interface ThemeSlice {
  activePresetId: string | null
  designSystem: DesignSystemState
  setDesignSystemOption: <K extends keyof DesignSystemState>(key: K, value: DesignSystemState[K]) => void
  setDesignSystemBulk: (options: Partial<DesignSystemState>) => void
  shuffleDesignSystem: () => void

  setAppDesignPreset: (presetId: string) => void
  updateAppDesignColor: (key: string, value: string) => void
  setAppDesignFontFamily: (font: string) => void
  setAppDesignRadiusScale: (scale: string) => void

  /** @deprecated Use setAppDesignPreset for generated-application design tokens. */
  setThemePreset: (presetId: string) => void
  /** @deprecated Use updateAppDesignColor for generated-application design tokens. */
  updateThemeColor: (key: string, value: string) => void
  /** @deprecated Use setAppDesignFontFamily for generated-application design tokens. */
  setFontFamily: (font: string) => void
  /** @deprecated Use setAppDesignRadiusScale for generated-application design tokens. */
  setRadiusScale: (scale: string) => void
  resetTheme: () => void
}

type FullState = ThemeSlice & {
  currentProject: any
  isDirty: boolean
  hasUncommittedArtifactLineage: boolean
}
type Set = (fn: (state: FullState) => void) => void
type Get = () => FullState

export function createThemeSlice(set: Set, _get: Get, _api: unknown): ThemeSlice {
  function applyAppDesignPreset(state: FullState, presetId: string): void {
    const preset = THEME_PRESETS.find((p) => p.id === presetId)
    if (!preset || !state.currentProject) return

    const tokens = state.currentProject.tokens ?? {}
    state.currentProject.tokens = {
      ...tokens,
      colors: { ...preset.colors },
      fontFamily: preset.fontFamily,
      radius: RADIUS_MAP[preset.radius] ?? RADIUS_MAP.md,
    }
    state.activePresetId = presetId
    markProjectContentDirty(state)
  }

  return {
    activePresetId: null,
    designSystem: { ...DEFAULT_DESIGN_SYSTEM },

    setDesignSystemOption: (key, value) => {
      set((s) => { s.designSystem[key] = value })
    },

    setDesignSystemBulk: (options) => {
      set((s) => { Object.assign(s.designSystem, options) })
    },

    shuffleDesignSystem: () => {
      set((s) => {
        const pick = <T extends { value: string }>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)].value
        s.designSystem = {
          style: pick(STYLE_OPTIONS) as DesignSystemState['style'],
          baseColor: pick(BASE_COLOR_OPTIONS) as DesignSystemState['baseColor'],
          theme: pick(THEME_OPTIONS) as DesignSystemState['theme'],
          chart: pick(CHART_OPTIONS) as DesignSystemState['chart'],
          radius: pick(RADIUS_OPTIONS) as DesignSystemState['radius'],
          font: pick(FONT_OPTIONS) as DesignSystemState['font'],
        }
      })
    },

    setAppDesignPreset: (presetId) => {
      set((s) => { applyAppDesignPreset(s, presetId) })
    },

    updateAppDesignColor: (key, value) => {
      set((s) => {
        if (!s.currentProject) return
        if (!s.currentProject.tokens) s.currentProject.tokens = {}
        if (!s.currentProject.tokens.colors) s.currentProject.tokens.colors = {}
        s.currentProject.tokens.colors[key] = value
        markProjectContentDirty(s)
      })
    },

    setAppDesignFontFamily: (font) => {
      set((s) => {
        if (!s.currentProject) return
        if (!s.currentProject.tokens) s.currentProject.tokens = {}
        s.currentProject.tokens.fontFamily = font
        markProjectContentDirty(s)
      })
    },

    setAppDesignRadiusScale: (scale) => {
      set((s) => {
        if (!s.currentProject) return
        if (!s.currentProject.tokens) s.currentProject.tokens = {}
        s.currentProject.tokens.radius = resolveRadiusScale(scale)
        markProjectContentDirty(s)
      })
    },

    setThemePreset: (presetId) => {
      set((s) => { applyAppDesignPreset(s, presetId) })
    },

    updateThemeColor: (key, value) => {
      set((s) => {
        if (!s.currentProject) return
        if (!s.currentProject.tokens) s.currentProject.tokens = {}
        if (!s.currentProject.tokens.colors) s.currentProject.tokens.colors = {}
        s.currentProject.tokens.colors[key] = value
        markProjectContentDirty(s)
      })
    },

    setFontFamily: (font) => {
      set((s) => {
        if (!s.currentProject) return
        if (!s.currentProject.tokens) s.currentProject.tokens = {}
        s.currentProject.tokens.fontFamily = font
        markProjectContentDirty(s)
      })
    },

    setRadiusScale: (scale) => {
      set((s) => {
        if (!s.currentProject) return
        if (!s.currentProject.tokens) s.currentProject.tokens = {}
        s.currentProject.tokens.radius = resolveRadiusScale(scale)
        markProjectContentDirty(s)
      })
    },

    resetTheme: () => {
      set((s) => { applyAppDesignPreset(s, 'modern') })
    },
  }
}
